<script setup lang="ts">
import { ref, toRef } from 'vue';
import { UUID } from '@hfc/common/src/types/UUID';
import { ModalDialog, router } from '@hfc/ui-core';
import { Field, useForm } from 'vee-validate';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';

const { appStore, appState } = useAppStore();

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);
const { getRouteNameByAppType } = useUrlHelper();
const { handleSfApiError } = useHandleApiError();

interface ComponentProps {
  applicationType: string;
  uuid: UUID;
  email?: string;
  application: unknown;
  callback?: (email: string) => Promise<void>;
}

const props = defineProps<ComponentProps>();

const appType = toRef(props.applicationType as AppTypeDentalOrVision);

interface EmitsInterface {
  (event: 'close'): void;
}

const emits = defineEmits<EmitsInterface>();

const email = ref(props.email ?? '');

const confirmSaveAndExit = async ():Promise<void> => {
  const valid = await runValidations();
  if (valid) {
    if (props.callback) {
      await props.callback(email.value);
    }

    const result = await appStore.saveAndExit(appType.value, email.value, props.application, props.uuid);
    if (result.error) {
      handleSfApiError(result.error);

      return;
    }
    if (result.success) {
      localStorage.setItem(`${appType.value}SaveAndExit`, 'true');
      router.push({ name: getRouteNameByAppType(appType.value, 'SaveAndExit') });
      emits('close');

      return;
    }
  }
};
</script>

<template>
  <ModalDialog title="Important!" :loading="appState.loading" @close="emits('close')" @confirm="confirmSaveAndExit" confirm-button="Yes" cancel-button="Close" :close-on-confirm="false">
    <template #content>
      <div class="flex">
        <p>Are you sure you want to save and exit? If so, everything you've entered so far will be saved and you will receive an email with a link to resume your application.</p>
      </div>
      <div class="flex justify-center">
        <div class="w-full md:w-1/2">
          <Field v-model="email" type="text" v-slot="{ field, errorMessage, meta }" name="Email" rules="required|email">
            <input placeholder="Email" v-autofocus id="Email" v-bind="field" :class="{ ...meta }">
            <span class="error">{{ errorMessage }}</span>
          </Field>
        </div>
      </div>
    </template>
  </ModalDialog>
</template>
